import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { ConnectLogicService, ConnectState } from '@services/ymodem/connect-logic.service';
import { YmodemUploadService } from '@services/ymodem/ymodem-upload.service';
import { SerialService } from '@services/ymodem/serial.service';
import { DeviceInfoService } from '@services/ymodem/device-info.service';
import { DeviceVerificationService } from '@services/ymodem/device-verification.service';
import { map, Observable, Subscription, throttleTime } from 'rxjs';
import { CachedValuesService } from '@services/cached-values/cached-values.service';
import { LocationModel } from 'app/types/location.type';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { Permission } from 'app/enums/permission.enum';

@Component({
    selector: 'app-measurement-import',
    standalone: false,
    templateUrl: './measurement-import.component.html',
    styleUrls: ['./measurement-import.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeasurementImportComponent implements OnInit, OnDestroy {
    private state: ConnectState = {
        isConnected: false,
        isConnecting: false,
        isSupported: false,
        connectionError: null,
        currentDevice: null,
        receivedData: [],
        isTransferring: false,
        transferProgress: 0,
        transferSpeedBps: 0,
        currentFileName: '',
        filesReceived: 0,
        uploadStatus: {
            pending: 0,
            uploading: 0,
            completed: 0,
            failed: 0,
            totalFiles: 0
        },
        fileList: []
    };

    public showLog = false;
    public showManuallyImport = false;
    public skipUpload = false;
    private connected = false;
    private subscription?: Subscription;
    private logicSubscription?: Subscription;
    public isDragOver = false;
    private _lastFileListRef: ConnectState['fileList'] = [];
    private _sortedFileList: ConnectState['fileList'] = [];
    public selectedLocationId: string | null = null;
    public permissions = Permission;
    private locationNameMap: Map<string, string> = new Map();

    public constructor(
        private connectLogic: ConnectLogicService,
        private uploadService: YmodemUploadService,
        private serialService: SerialService,
        private deviceInfoService: DeviceInfoService,
        private deviceVerificationService: DeviceVerificationService,
        private cachedValuesService: CachedValuesService,
        private cdr: ChangeDetectorRef
    ) {
        void connectLogic.cleanupUploadedFiles();
    }

    public readonly locations$: Observable<DropdownItem<string>[]> = this.cachedValuesService.locations$.pipe(
        map((data) =>
            data.map((item: LocationModel) => {
                return {
                    label: item.name,
                    value: item.id
                } as DropdownItem<string>;
            })
        )
    );

    public ngOnInit(): void {
        // Subscribe to locations to build location name map
        this.cachedValuesService.locations$.subscribe((locations: LocationModel[]) => {
            this.locationNameMap.clear();
            locations.forEach((location) => {
                this.locationNameMap.set(location.id, location.name);
            });
        });

        // Unthrottled: handles connection logic only
        this.logicSubscription = this.connectLogic.state$.subscribe((state) => {
            if (!this.connected && state.isConnected && !state.isTransferring) {
                setTimeout(() => {
                    void this.onTransfer();
                }, 100);
            }
            this.connected = state.isConnected;
        });

        // Throttled: drives UI updates at most every 200 ms during fast transfer
        this.subscription = this.connectLogic.state$
            .pipe(throttleTime(200, undefined, { leading: true, trailing: true }))
            .subscribe((state) => {
                this.state = state;
                this.cdr.markForCheck();
            });
    }

    public ngOnDestroy(): void {
        void this.connectLogic.cleanupUploadedFiles();
        this.connectLogic.clearReceivedData();
        this.logicSubscription?.unsubscribe();
        this.subscription?.unsubscribe();
        void this.connectLogic.disconnect();
    }

    // Event handlers that call service methods
    public async onConnect(): Promise<void> {
        await this.connectLogic.cleanupUploadedFiles();
        await this.connectLogic.connect();
    }

    public async onDisconnect(): Promise<void> {
        await this.connectLogic.disconnect();
    }

    public async onTransfer(): Promise<void> {
        // Set skip upload flag before starting transfer
        this.uploadService.setSkipUpload(this.skipUpload);
        await this.connectLogic.transfer(this.selectedLocationId, async () => this.performDeviceVerification());
    }

    public onSkipUploadChange(): void {
        // Sync checkbox state to upload service immediately
        this.uploadService.setSkipUpload(this.skipUpload);
    }

    public onClearData(): void {
        this.connectLogic.clearReceivedData();
    }

    public async onRefreshUploadStatus(): Promise<void> {
        await this.connectLogic.updateUploadStatus();
    }

    public async onRetryFailedUploads(): Promise<void> {
        await this.connectLogic.retryFailedUploads();
    }

    public async onRetryUpload(): Promise<void> {
        await this.connectLogic.retryUpload();
    }

    public async onClearStoredFiles(): Promise<void> {
        await this.connectLogic.clearStoredFiles();
    }

    // File import handlers
    public onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            void this.importFiles(Array.from(input.files));
            input.value = ''; // Reset input to allow re-selecting same file
        }
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            void this.importFiles(Array.from(event.dataTransfer.files));
        }
    }

    private async importFiles(files: File[]): Promise<void> {
        for (const file of files) {
            try {
                const content = await this.readFileContent(file);
                await this.connectLogic.importFileManually(file.name, content, this.selectedLocationId);
            } catch (error) {
                console.error(`Failed to import file ${file.name}:`, error);
            }
        }
    }

    private async readFileContent(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to read file as text'));
                }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
        });
    }

    // Utility methods for template
    public trackByIndex(index: number): number {
        return index;
    }

    public trackById(index: number, item: { id?: number }): number {
        return typeof item.id === 'number' ? item.id : index;
    }

    // Helper methods for unified file pipeline
    public getStatusIcon(status: string): string {
        switch (status) {
            case 'transferring':
                return 'download';
            case 'pending':
            case 'queued':
                return 'schedule';
            case 'uploading':
                return 'cloud_upload';
            case 'completed':
                return 'check_circle';
            case 'failed':
                return 'error';
            default:
                return 'help';
        }
    }

    public getStatusText(status: string): string {
        switch (status) {
            case 'transferring':
                return 'Transferring';
            case 'pending':
                return 'Queued';
            case 'uploading':
                return 'Uploading';
            case 'completed':
                return 'Completed';
            case 'failed':
                return 'Failed';
            default:
                return status;
        }
    }

    public getLocationName(locationId?: string): string {
        if (!locationId) {
            return 'Unknown';
        }
        return this.locationNameMap.get(locationId) || locationId;
    }

    // Expose state properties for easier template access
    public get isConnected() {
        return this.state.isConnected;
    }
    public get isConnecting() {
        return this.state.isConnecting;
    }
    public get isSupported() {
        return this.state.isSupported;
    }
    public get connectionError() {
        return this.state.connectionError;
    }
    public get currentDevice() {
        return this.state.currentDevice;
    }
    public get receivedData() {
        return this.state.receivedData;
    }
    public get isTransferring() {
        return this.state.isTransferring;
    }
    public get isUploadInProgress(): boolean {
        return this.state.uploadStatus.uploading > 0;
    }
    public get transferProgress() {
        return this.state.transferProgress;
    }
    public get currentFileName() {
        return this.state.currentFileName;
    }
    public get filesReceived() {
        return this.state.filesReceived;
    }
    public get uploadStatus() {
        return this.state.uploadStatus;
    }
    public get fileList() {
        const list = this.state.fileList;
        if (list !== this._lastFileListRef) {
            this._lastFileListRef = list;
            this._sortedFileList = [...list].sort((a, b) => b.timestamp - a.timestamp);
        }
        return this._sortedFileList;
    }
    public get transferSpeedBps() {
        return this.state.transferSpeedBps;
    }

    private async performDeviceVerification(): Promise<void> {
        try {
            console.log('[Import] Preparing device for verification...');

            // Initialize serial communication with buffered reading
            // Required for waitForTextResponse() which device verification uses
            this.serialService.startBufferedReading();
            this.serialService.drainBuffer();

            // Give device a moment to stabilize
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Send a return character to ensure device is ready
            console.log('[Import] Sending return character to stabilize device...');
            void this.serialService.sendData(new TextEncoder().encode('\r\n'));

            // Consume any buffered responses
            let consumedLines = 0;
            while (consumedLines < 5) {
                try {
                    const line = await this.serialService.waitForTextResponse(100);
                    console.log(`[Import] Consumed buffered line ${consumedLines + 1}: "${line}"`);
                    consumedLines++;
                } catch {
                    // No more buffered data
                    break;
                }
            }

            // Wait a brief moment before verification
            await new Promise((resolve) => setTimeout(resolve, 100));

            console.log('[Import] Performing device verification...');

            // Call the verification service with serial service
            // This retrieves version, serial number, and verifies with server
            // All errors are logged to console and do not block transfer
            const verificationResult = await this.deviceVerificationService.verifyDevice(
                this.serialService,
                false,
                null,
                true
            );

            if (verificationResult.deviceInfo) {
                console.log('[Import] ✓ Device verification completed');
            } else {
                console.warn('[Import] ✗ Device verification failed to retrieve device info');
            }

            // Clean up serial state after verification to prepare device for YModem transfer
            console.log('[Import] Preparing device for file transfer...');
            this.serialService.drainBuffer();

            // Send final reset to put device back into normal command state
            void this.serialService.sendData(new TextEncoder().encode('\r\n'));

            // Give device time to process and stabilize before transfer
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Drain any response data
            this.serialService.drainBuffer();

            // CRITICAL: Stop buffered reading before transfer
            // Transfer needs direct serial control for YModem protocol (szGetPacket)
            console.log('[Import] Stopping buffered reading to enable transfer mode...');
            this.serialService.stopBufferedReading();
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.warn(`[Import] Device verification encountered an error: ${errorMsg}`);
            // Don't block transfer - continue anyway
        } finally {
            this.serialService.stopBufferedReading();
        }
    }
}
